import React, { Fragment } from "react";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import { useFormikContext } from "formik";
import DebounceTextarea from "components/DebounceTextarea";
import classnames from "classnames";
import { STATE_KEYS } from "utils/constants/state";
import { useAppCheckout } from "utils/selectors";
import OptionListPicker from "components/OptionListPicker";
import { getUUID } from "utils/uuid";
import { LIST_PICKER_CUSTOM_CLASSES } from "utils/constants/ui";
import { useIsManifestEditMode } from "utils/hooks/manifest";
import FieldListFactory from "components/FieldListFactory";

const ValuesFields = ({ name }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const isEditMode = useIsManifestEditMode();

  // Header not used yet
  // const headerPath = getFieldPath(name, STATE_KEYS.FILE.HEADER);
  // const headerValue = get(values, headerPath);

  const titlePath = getFieldPath(name, STATE_KEYS.FILE.TITLE, "");
  const titleValue = get(values, titlePath);

  const promptPath = getFieldPath(name, STATE_KEYS.FILE.PROMPT, "");
  const promptValue = get(values, promptPath);
  const checkout = useAppCheckout();

  const fileAllowedTypeOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.FILE_ALLOWED_TYPES) || [];
  const allowedTypesPath = getFieldPath(name, STATE_KEYS.FILE.ALLOWED_TYPES);
  const allowedTypesValue = get(values, allowedTypesPath) || [];
  const selectedTypes = allowedTypesValue.map(({ uid }) => uid);

  const allowedTypeProps = {
    label: "Allowed types",
    value: selectedTypes,
    name: allowedTypesPath,
    multiselect: true,
    options: fileAllowedTypeOptions,
    disabled: isEditMode,
    onClick: (_, selectedValue) => {
      const updatedTypes = cloneDeep(allowedTypesValue);
      const matchIndex =
        Array.isArray(allowedTypesValue) &&
        allowedTypesValue.findIndex(({ uid }) => uid === selectedValue);

      let shouldUpdate = false;
      if (matchIndex >= 0) {
        shouldUpdate = true;
        updatedTypes.splice(matchIndex, 1);
      } else {
        shouldUpdate = true;
        updatedTypes.push({
          uid: selectedValue,
          uuid: getUUID()
        });
      }

      if (shouldUpdate) {
        setFieldValue(allowedTypesPath, updatedTypes);
        if (!get(touched, allowedTypesPath)) {
          setFieldTouched(name, true);
        }
      }
    },
    canUnset: true,
    customClasses: LIST_PICKER_CUSTOM_CLASSES
  };

  const fileFromSourceOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.FILE_FROM_SOURCES) || [];
  const fromSourcesPath = getFieldPath(name, STATE_KEYS.FILE.FROM_SOURCES);
  const fromSourcesValue = get(values, fromSourcesPath) || [];
  const selectedSources = fromSourcesValue.map(({ uid }) => uid);

  const fromSourcesProps = {
    label: "From sources",
    value: selectedSources,
    name: fromSourcesPath,
    multiselect: true,
    options: fileFromSourceOptions,
    disabled: isEditMode,
    onClick: (_, selectedValue) => {
      const updatedTypes = cloneDeep(fromSourcesValue);
      const matchIndex =
        Array.isArray(fromSourcesValue) &&
        fromSourcesValue.findIndex(({ uid }) => uid === selectedValue);

      let shouldUpdate = false;
      if (matchIndex >= 0) {
        shouldUpdate = true;
        updatedTypes.splice(matchIndex, 1);
      } else {
        shouldUpdate = true;
        updatedTypes.push({
          uid: selectedValue,
          uuid: getUUID()
        });
      }

      if (shouldUpdate) {
        setFieldValue(fromSourcesPath, updatedTypes);
        if (!get(touched, fromSourcesPath)) {
          setFieldTouched(name, true);
        }
      }
    },
    canUnset: true,
    customClasses: LIST_PICKER_CUSTOM_CLASSES
  };

  return (
    <Fragment>
      <FieldListFactory
        name={name}
        config={[
          {
            name: "label"
          },
          {
            name: "description"
          }
        ]}
      />
      <div className="pt3">
        <div className="f5 pb2 fw6">Presentation</div>
        <div className="f6 lh-copy">
          Control the labels for your identity field.
        </div>
        <div className="w-100 pt3">
          {[
            {
              label: "Title",
              name: titlePath,
              uid: "title",
              placeholder: "Set a title",
              value: titleValue
            },
            {
              label: "Prompt",
              uid: "prompt",
              name: promptPath,
              placeholder: "Set a prompt",
              value: promptValue
            }
          ].map((field, fieldIx) => (
            <div
              key={field.uid}
              className={classnames("w-100", {
                pt3: fieldIx
              })}
            >
              <label className="f6 mb2 dib fw6">{field.label}</label>
              <DebounceTextarea
                inputValue={field.value}
                debounce={500}
                minRows={1}
                disabled={isEditMode}
                name={field.name}
                valueSet={(val) => {
                  setFieldValue(field.name, val);
                  if (!get(touched, field.name)) {
                    setFieldTouched(field.name, true);
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-column pt3">
        <div className="w-100">
          <div className="f5 pb2 fw6">Allowed file types</div>
          <div className="f6 lh-copy">
            Set which file types can be uploaded.
          </div>
          <div className="f7 lh-copy pt1">
            When none are selected then all will be allowed.
          </div>
          <div className="pt3">
            <OptionListPicker {...allowedTypeProps} />
          </div>
        </div>
      </div>
      <div className="flex flex-column pt3">
        <div className="w-100">
          <div className="f5 pb2 fw6">From sources</div>
          <div className="f6 lh-copy">
            Set which file sources you allow uploads from.
          </div>
          <div className="f7 lh-copy pt1">
            When none are selected then all will be allowed.
          </div>
          <div className="pt3">
            <OptionListPicker {...fromSourcesProps} />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ValuesFields;
